import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  cn,
} from '@newfront-insurance/core-ui/v2';
import { useId, useState, type ComponentType } from 'react';

import type { SettingsEmployee } from './employee-utils';
import { OrganizationPeoplePicker, type ExcludedLabel } from './organization-people-picker';
import { SettingsPeoplePicker, type PeoplePickerProps, type SettingsPeopleEligibility } from './people-picker';
import { useSelectionSubmission } from '../settings-shared/use-selection-submission';

export interface SettingsPeopleSubmitResult {
  failedUserUuids: readonly string[];
  error?: string;
}

/**
 * Shared dialog presentation. Its caller owns eligibility, mutations, and refreshes. Mount only while open.
 * Passing `organizationUuid` lists that organization's people to browse; otherwise the picker is a type-ahead.
 */
export function SettingsPeopleDialog({
  title = 'Add people',
  description,
  hideDescription = false,
  submitLabel = 'Add people',
  disabled = false,
  excludedUserUuids,
  eligibility,
  organizationUuid,
  excludedLabel,
  onClose,
  onSubmit,
  picker,
  inputLabel = 'Add teammates',
  pendingLabel = 'Adding...',
  single = false,
}: {
  title?: string;
  description: string;
  hideDescription?: boolean;
  submitLabel?: string;
  disabled?: boolean;
  excludedUserUuids: Set<string>;
  eligibility?: SettingsPeopleEligibility;
  organizationUuid?: string | null;
  /** Shown beside people who are already added, when browsing an organization. */
  excludedLabel?: ExcludedLabel;
  onClose: () => void;
  onSubmit: (employees: SettingsEmployee[]) => Promise<SettingsPeopleSubmitResult>;
  picker?: ComponentType<PeoplePickerProps>;
  inputLabel?: string;
  pendingLabel?: string;
  /** Choose one person: picking someone else replaces the selection. Only the organization browser draws radios. */
  single?: boolean;
}) {
  const inputId = useId();
  const [selectedEmployees, setSelectedEmployees] = useState<SettingsEmployee[]>([]);
  const browsesOrganization = organizationUuid !== undefined && !picker;
  const Picker = picker ?? SettingsPeoplePicker;
  const isSubmitDisabled = disabled || !!eligibility?.loading || !!eligibility?.error;
  const { isSaving, error, submit, close } = useSelectionSubmission({
    selected: selectedEmployees,
    getKey: (employee) => employee.userUuid,
    disabled: isSubmitDisabled,
    onSubmit: async (employees) => {
      const result = await onSubmit(employees);
      return { failedKeys: result.failedUserUuids, error: result.error };
    },
    onSettled: (failed) => setSelectedEmployees(failed),
    onClose,
    partialFailureMessage: "Couldn't save changes for everyone. Your remaining selections are kept. Try again.",
    failureMessage: "Couldn't save changes. Your selections are kept. Try again.",
  });

  const addEmployee = (employee: SettingsEmployee) =>
    setSelectedEmployees((current) => {
      if (single) return [employee];
      return current.some((item) => item.userUuid === employee.userUuid) ? current : [...current, employee];
    });
  const removeEmployee = (userUuid: string) =>
    setSelectedEmployees((current) => current.filter((item) => item.userUuid !== userUuid));

  return (
    <AlertDialog
      open
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <AlertDialogContent
        className={cn(
          "w-[398px] max-w-[calc(100vw-32px)] gap-5 rounded-[4px] p-6 font-['Open_Sans',sans-serif]",
          browsesOrganization && 'w-[560px]',
        )}
      >
        <AlertDialogHeader className="flex-col">
          <AlertDialogTitle className="text-steel-500 text-base leading-6 font-semibold">{title}</AlertDialogTitle>
          <AlertDialogDescription className={hideDescription ? 'sr-only' : 'text-steel-400 text-sm'}>
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="flex flex-col gap-1.5">
          {/* The browse picker's search box names itself, and the dialog title already says who is being added. */}
          {browsesOrganization ? null : (
            <label htmlFor={inputId} className="text-steel-500 text-xs leading-4 font-semibold">
              {inputLabel}
            </label>
          )}
          {browsesOrganization ? (
            <OrganizationPeoplePicker
              organizationUuid={organizationUuid}
              excludedLabel={excludedLabel}
              inputId={inputId}
              excludedUserUuids={excludedUserUuids}
              selectedEmployees={selectedEmployees}
              disabled={disabled || isSaving}
              single={single}
              onAddEmployee={addEmployee}
              onRemoveEmployee={removeEmployee}
            />
          ) : (
            <Picker
              inputId={inputId}
              excludedUserUuids={excludedUserUuids}
              eligibility={eligibility}
              selectedEmployees={selectedEmployees}
              disabled={disabled || isSaving}
              onAddEmployee={addEmployee}
              onRemoveEmployee={removeEmployee}
            />
          )}
        </div>
        {error ? (
          <p role="alert" className="text-fire-500 text-sm">
            {error}
          </p>
        ) : null}
        <AlertDialogFooter className="flex-row justify-end gap-2">
          <AlertDialogCancel disabled={isSaving}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={isSubmitDisabled || !selectedEmployees.length || isSaving}
            onClick={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            {isSaving ? pendingLabel : submitLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
